/* Karpet24 guide + policy hub pages.
   1. <k24-scrollspy>: marks the in-page link whose target was passed last
      (aria-current="location"). The page works without it; the links are plain anchors.
   2. The phone table of contents closes after a tap, so the reader lands on the text.
   3. Guide tables with three or more columns get a data-label on every cell, which
      lets the CSS turn each row into a small card on a phone. Tables with merged
      cells keep the plain horizontal scroll. */
(() => {
  if (!customElements.get('k24-scrollspy')) {
    class K24Scrollspy extends HTMLElement {
      #links = [];
      #targets = [];
      #frame = 0;
      #onScroll = () => {
        if (this.#frame) return;
        this.#frame = requestAnimationFrame(() => {
          this.#frame = 0;
          this.#update();
        });
      };

      connectedCallback() {
        for (const link of this.querySelectorAll('a[href^="#"]')) {
          const id = decodeURIComponent(link.hash.slice(1));
          const target = id ? document.getElementById(id) : null;
          if (!target) continue;
          this.#links.push(link);
          this.#targets.push(target);
        }
        if (!this.#targets.length) return;
        window.addEventListener('scroll', this.#onScroll, { passive: true });
        window.addEventListener('resize', this.#onScroll, { passive: true });
        this.#update();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.#onScroll);
        window.removeEventListener('resize', this.#onScroll);
        if (this.#frame) cancelAnimationFrame(this.#frame);
      }

      #update() {
        const offset = Number.parseFloat(this.dataset.offset || '0.3');
        const line = window.innerHeight * offset;
        let active = -1;
        for (const [index, target] of this.#targets.entries()) {
          if (target.getBoundingClientRect().top - line <= 0) active = index;
        }
        const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4;
        if (atEnd && active > -1) active = this.#targets.length - 1;
        for (const [index, link] of this.#links.entries()) {
          if (index === active) {
            link.setAttribute('aria-current', 'location');
          } else {
            link.removeAttribute('aria-current');
          }
        }
      }
    }
    customElements.define('k24-scrollspy', K24Scrollspy);
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('.k24-guide__toc-mobile a[href^="#"]');
    if (!link) return;
    link.closest('details')?.removeAttribute('open');
  });

  const labelTables = () => {
    for (const wrapper of document.querySelectorAll('.k24-guide__table')) {
      const table = wrapper.querySelector('table');
      if (!table || wrapper.classList.contains('k24-guide__table--stack')) continue;
      const heads = [...table.querySelectorAll('thead th')].map((th) => th.textContent.trim());
      const merged = table.querySelector('[colspan], [rowspan]');
      if (heads.length < 3 || merged) continue;
      for (const row of table.querySelectorAll('tbody tr')) {
        for (const [index, cell] of [...row.children].entries()) {
          if (heads[index]) cell.dataset.label = heads[index];
        }
      }
      wrapper.classList.add('k24-guide__table--stack');
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', labelTables, { once: true });
  } else {
    labelTables();
  }
})();
